import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { resetStore } from './resetStore';
import { getServerUrl } from '../../Utils/serverSelector';
import { saveJwtToken, clearJwtToken, getJwtToken, clearUserClientUid } from '../../Utils/authStorage';
import { apiRequest } from '../../Utils/apiClient';

const ALLOWED_TOKEN = process.env.REACT_APP_ALLOWED_TOKEN;

export const headers = {
  Authorization: `Bearer ${ALLOWED_TOKEN}`,
  'Content-Type': 'application/json',
};

// ===============================
//   sessionStorage helpers
// ===============================

const SESSION_KEY = 'user_account';

const saveToSession = (account) => {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(account)); } catch { }
};

const loadFromSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const clearSession = () => {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { }
};

// ===============================
//   Async Thunks
// ===============================

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async (phone, thunkAPI) => {
    try {
      const normalizedPhone = String(phone || "")
        .trim()
        .replace(/[^\d+]/g, "");

      if (!normalizedPhone) {
        return thunkAPI.rejectWithValue({
          message: "Не указан номер телефона",
        });
      }

      // Убираем старый кэш клиента, чтобы fetchUserSession
      // не подхватил предыдущий аккаунт.
      clearSession();

      const res = await axios.post(
        `${getServerUrl()}/auth/user-login`,
        {
          phone: normalizedPhone,
          switchFromMaster: true,
        },
        {
          headers,
          withCredentials: true,
        }
      );

      const token = res?.data?.token;
      const account = res?.data?.account;

      if (!token) {
        throw new Error("Сервер не вернул клиентский JWT");
      }

      if (!account) {
        throw new Error("Сервер не вернул клиентский аккаунт");
      }

      saveJwtToken(token);
      saveToSession(account);

      return account;
    } catch (error) {
      console.error("[loginUser]", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });

      return thunkAPI.rejectWithValue(
        error?.response?.data || {
          message: error?.message || "Login error",
        }
      );
    }
  }
);

export const fetchUserSession = createAsyncThunk(
  'user/fetchUserSession',
  async (_, thunkAPI) => {
    // 1. sessionStorage (работает пока вкладка открыта)
    const cached = loadFromSession();
    if (cached) return cached;

    // 2. JWT из localStorage → запрос на бэк
    const jwtToken = getJwtToken();
    if (!jwtToken) {
      return thunkAPI.rejectWithValue({ status: 401, message: 'No token' });
    }

    try {
      const res = await axios.get(
        `${getServerUrl()}/auth/user-session`,
        {
          headers: { ...headers, Authorization: `Bearer ${jwtToken}` },
          withCredentials: true,
        }
      );
      // ✅ сохраняем в sessionStorage чтобы следующий рендер был быстрым
      saveToSession(res.data);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        status: err.response?.status || null, // null = сетевая ошибка (оффлайн/таймаут)
        ...(typeof err.response?.data === 'object'
          ? err.response.data
          : { message: err.response?.data || 'Session error' }),
      });
    }
  }
);

// ── Handoff: одноразовый токен для перехода zmanup.com → zmanup.co.il ──
export const createBookingHandoff = createAsyncThunk(
  'user/createBookingHandoff',
  async (_, thunkAPI) => {
    try {
      const res = await apiRequest('POST', '/auth/client-handoff');
      return res?.data?.handoffToken || res?.handoffToken || null;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || 'Handoff error');
    }
  }
);

// ── Handoff: обмен токена из URL (?ct=...) на сессию клиента ──
export const exchangeHandoffToken = createAsyncThunk(
  'user/exchangeHandoffToken',
  async (handoffToken, thunkAPI) => {
    try {
      const res = await axios.post(
        `${getServerUrl()}/auth/client-handoff/exchange`,
        { handoffToken },
        { headers, withCredentials: true }
      );
      if (res.data.token) {
        saveJwtToken(res.data.token);
      }
      return res.data.account;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || 'Handoff error');
    }
  }
);

export const logoutUserSession = createAsyncThunk(
  'user/logoutUser',
  async () => {
    clearSession();
    clearJwtToken();
    clearUserClientUid(); // ✅ ДОБАВИТЬ
    await axios.post(
      `${getServerUrl()}/auth/logout`,
      {},
      { headers, withCredentials: true }
    );
    return true;
  }
);

// ✅ ИСПРАВЛЕНО: apiRequest автоматически подставляет JWT из localStorage для iOS
export const updateUserAccount = createAsyncThunk(
  'user/updateUserAccount',
  async ({ uid, updates }, thunkAPI) => {
    try {
      const res = await apiRequest('PATCH', `/users/${uid}`, updates);
      return res?.data?.updatedUser || res?.updatedUser || { ...updates, uid };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || 'Update error');
    }
  }
);

export const switchMasterToClient = createAsyncThunk(
  "user/switchMasterToClient",
  async (_, thunkAPI) => {
    try {
      clearSession();

      const masterJwt = getJwtToken();

      const res = await axios.post(
        `${getServerUrl()}/auth/switch-master-to-client`,
        {},
        {
          headers: {
            ...headers,
            Authorization: `Bearer ${masterJwt}`,
          },
          withCredentials: true,
        }
      );

      const token = res?.data?.token;
      const account = res?.data?.account;

      if (!token || !account) {
        throw new Error("Сервер не вернул клиентскую сессию");
      }

      saveJwtToken(token);
      saveToSession(account);

      return account;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || {
          error: error?.message || "Switch error",
        }
      );
    }
  }
);

// ===============================
//   Slice
// ===============================

export const userSessionSlice = createSlice({
  name: 'userSession',
  initialState: {
    currentAccount: null,
    loading: false,
    updateLoading: false,
    error: null,
    isAuthenticated: false,
  },
  reducers: {
    setCurrentUser: (state, action) => {
      state.currentAccount = action.payload;
      state.isAuthenticated = true;
      saveToSession(action.payload);
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.currentAccount = action.payload;
        state.isAuthenticated = true;
        state.loading = false;
        saveToSession(action.payload);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
        state.isAuthenticated = false;
      })

      .addCase(fetchUserSession.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUserSession.fulfilled, (state, action) => {
        state.currentAccount = action.payload;
        state.isAuthenticated = true;
        state.loading = false;
        saveToSession(action.payload);
      })
      .addCase(fetchUserSession.rejected, (state, action) => {
        state.error = action.payload;
        state.isAuthenticated = false;
        state.loading = false;
        // чистим только при реальном отказе авторизации,
        // а не при сетевой ошибке (оффлайн/таймаут)
        const status = action.payload?.status || action.payload?.error;
        if (status === 401 || status === 403 || action.payload === 'No local token') {
          clearSession();
        }
      })

      .addCase(updateUserAccount.pending, (state) => { state.updateLoading = true; state.error = null; })
      .addCase(updateUserAccount.fulfilled, (state, action) => {
        state.currentAccount = { ...state.currentAccount, ...action.payload };
        state.updateLoading = false;
        saveToSession(state.currentAccount);
      })
      .addCase(updateUserAccount.rejected, (state, action) => {
        state.error = action.payload;
        state.updateLoading = false;
      })

      .addCase(logoutUserSession.fulfilled, (state) => {
        state.currentAccount = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
      })

      .addCase(exchangeHandoffToken.fulfilled, (state, action) => {
        state.currentAccount = action.payload;
        state.isAuthenticated = true;
        state.loading = false;
        saveToSession(action.payload);
      })

      .addCase(switchMasterToClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(switchMasterToClient.fulfilled, (state, action) => {
        state.currentAccount = action.payload;
        state.isAuthenticated = true;
        state.loading = false;
        saveToSession(action.payload);
      })
      .addCase(switchMasterToClient.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload;
      })

      .addCase(resetStore, (state) => {
        state.currentAccount = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.updateLoading = false;
        state.error = null;
        clearSession();
        clearJwtToken();
      });
  }
});

export default userSessionSlice.reducer;
export const { setCurrentUser } = userSessionSlice.actions;
